import * as React from 'react';
import { useNotify, useTranslate } from 'ra-core';
import { Edit, useEditContext, Toolbar, ToolbarClasses, SaveButton, Button, useRecordContext, useDelete, useResourceContext, RaRecord, DeleteWithUndoButtonProps, useDeleteWithUndoController, TextInput } from 'react-admin';
import { useCallback, useMemo } from 'react';
import { alpha, styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import { UserForm } from './UserForm';
import { defaultValues } from './defaultValues';
import { RecordEditTitle } from '../../components/RecordTitle';
import { isProduction } from '../../lib//permissions/isEnvironment';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { usePermissionGuard } from '../../lib/permissions/usePermissionGuard';
import { Dialog, DialogTitle, TextField, Typography } from '@mui/material';
import { Password } from '@mui/icons-material';
import { dataProvider } from '../../lib/dataProvider';

const StyledButton = styled(Button, {
  name: 'RaDeleteWithUndoButton',
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  color: theme.palette.error.main,
  '&:hover': {
      backgroundColor: alpha(theme.palette.error.main, 0.12),
      // Reset on mouse devices
      '@media (hover: none)': {
          backgroundColor: 'transparent',
      },
  },
}));

export const UserEdit = (props: any) => {
  return (
    <Edit title={<RecordEditTitle property="firstName" /> } {...props}>
      <UserEditForm />
    </Edit>
  );
};

const DeleteButton = <RecordType extends RaRecord = any>(
  props: DeleteWithUndoButtonProps<RecordType>
) => {
  const t = useTranslate();
  const { guard } = usePermissionGuard();
  const record = useRecordContext(props);
  const resource = useResourceContext(props);
  const { handleDelete } = useDeleteWithUndoController({
      record,
      resource
  });

   return (guard.isAllowed("User", "Delete") ? (
    <StyledButton
      className={'ra-delete-button'}
      onClick={(e) => {
        if (!window.confirm(t('resources.users.labels.areYouSure'))) return;

        return handleDelete(e);
      }}
      label={t('resources.users.labels.delete')}
    >
      <DeleteIcon />
    </StyledButton>
  ) : null)
};

export interface SetPasswordDialiogProps {
  open: boolean;
  onClose: () => void;
}
const SetPasswordDialiog = (props: SetPasswordDialiogProps) => {
  const record = useRecordContext();
  const notify = useNotify();
  const translate = useTranslate();
  const { onClose, open } = props;
  const [password1, setPassword1] = React.useState('');
  const [password2, setPassword2] = React.useState('');

  const lengthCheck = password1.length >= 8;
  const numberCheck = /\d/.test(password1);
  const specialCharacterCheck = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(password1);
  const upperCaseCheck = /[A-Z]/.test(password1);
  const lowerCaseCheck = /[a-z]/.test(password1);

  const setPasswordApiCall = useCallback(
    async (password: string) => {
      await dataProvider.post('/users/admin-set-password', {
        userId: record.id,
        password: password,
      }).then(() => notify(translate('resources.users.labels.password_succesfully_set'), { type: 'success'})).catch(() => {
        alert(translate('resources.users.labels.error_setting_password'));
      });
    }
  , [translate, notify, record.id]);

  const setPassword = () => {
    if(password1.length ===0) {
      alert(translate('resources.users.labels.pleaseSetPassword'));
      return;
    }

    if(password1 !== password2) {
      alert(translate('resources.users.labels.passwordsDoNotMatch'));
      return;
    }

    if(!lengthCheck || !numberCheck || !specialCharacterCheck || !upperCaseCheck || !lowerCaseCheck) {
      alert(translate('resources.users.validation.error'));
      return;
    }
    
    setPasswordApiCall(password1).then(() => onClose());    
  };

  return (
    <Dialog  onClose={onClose} open={open}>
      <DialogTitle>Set password</DialogTitle>
      <Typography variant="caption" gutterBottom style={{marginLeft: 24, marginRight: 24}}>
        {translate('resources.users.labels.password_requirements')}<br/>
        {lengthCheck ? '✔ ' : '-'} {translate('resources.users.validation.minimum_length')}<br/>
        {numberCheck ? '✔ ' : '-'} {translate('resources.users.validation.one_digit')}<br/>
        {specialCharacterCheck ? '✔ ' : '-'} {translate('resources.users.validation.special_character')}<br/>
        {upperCaseCheck ? '✔ ' : '-'} {translate('resources.users.validation.uppercase')}<br/>
        {lowerCaseCheck ? '✔ ' : '-'} {translate('resources.users.validation.lowercase')}<br/>
      </Typography>
      <TextField 
        type="password"
        style={{marginLeft: 24, marginRight: 24, marginTop : 15}} 
        label={translate('resources.users.fields.password')}
        defaultValue={""}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setPassword1(event.target.value);
        }}
      />
      <TextField 
        type="password"
        style={{marginLeft: 24, marginRight: 24, marginTop : 15}} 
        label={translate('resources.users.fields.confirmPassword')}
        defaultValue={""}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setPassword2(event.target.value);
        }}
      />
      <Button
        variant='contained'
        style={{marginLeft: 24, marginRight: 24, marginTop : 15, marginBottom : 7}}
        onClick={() => { 
          setPassword();
        }}
        label={translate('resources.users.labels.setPassword')}
      />
      <Button
        variant='outlined'
        style={{marginLeft: 24, marginRight: 24, marginBottom : 15}}
        onClick={() => { 
          onClose();
        }}
        label={translate('resources.users.labels.cancel')}
      />
    </Dialog>
  )
}

const CustomToolbar = (props) => {
  const { guard } = usePermissionGuard();
  const translate = useTranslate();
  const canSetPassword = guard.isAllowed('User', 'AdminSetPassword');
  const { save, record } = useEditContext();
  const [open, setOpen] = React.useState(false);
  const onBeforeSave = useCallback(
    (data) => {
      return save({ ...data, profiles: data.profiles.filter((p) => p.isActive || p.id) });
    },
    [save],
  );
  return (
    <>
    <Toolbar>
      <div className={ToolbarClasses.defaultToolbar}>
        <SaveButton />
        {canSetPassword ? (
          <Button
            onClick={() => { 
              setOpen(true);
            }}
            label={translate('resources.users.labels.setPassword')}
          >
            <Password />
          </Button>
        ) : null}
        {" "}
        { !isProduction() ? (
          <Button
            onClick={() => { 
              record.IsVerified = false;
              onBeforeSave(record);
            }}
            label="Reset account"
          >
            <RestartAltIcon />
          </Button>
        ) : null }
        <DeleteButton />
      </div>
    </Toolbar>
    <SetPasswordDialiog open={open} onClose={() => setOpen(false)} />
    </>
  )
};

// @todo diff (somehow) and exclude profiles.
function UserEditForm() {
  const { save, record, isFetching } = useEditContext();
  const onBeforeSave = useCallback(
    (data) => {
      return save({ ...data, profiles: data.profiles.filter((p) => p.isActive || p.id) });
    },
    [save],
  );
  const hydratedRecord = useMemo(() => {
    return {
      ...record,
      profiles: [
        record.profiles.find((profile) => profile.type === 'Dashboard') || defaultValues.profiles[0],
        record.profiles.find((profile) => profile.type === 'App') || defaultValues.profiles[1],
      ],
    };
  }, [record]);

  if (isFetching) return null;

  return <UserForm toolbar={<CustomToolbar />} onSubmit={onBeforeSave} record={hydratedRecord} />;
}
